const KPI_APP_URL = "https://truckx-independent-kpi.onrender.com/";
const KPI_APP_ORIGIN = new URL(KPI_APP_URL).origin;
const KPI_SSO_ASSERTION_URL = "https://truckx-kpi-sso.anurag-prajapat.workers.dev/kpi-sso/assertion";
const CHALLENGE_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;

let activeAttempt = null;

function report(onError, message) {
  if (typeof onError === "function") onError(message);
}

export function openKpiSsoHandoff({ windowRef = window, fetchImpl = windowRef.fetch.bind(windowRef), timeoutMs = 60_000, requestTimeoutMs = 30_000, acknowledgmentTimeoutMs = 45_000, onError } = {}) {
  if (activeAttempt) {
    report(onError, "KPI sign-in is already in progress. Wait for it to finish before retrying.");
    return false;
  }

  const bearer = windowRef.localStorage.getItem("token");
  if (typeof bearer !== "string" || bearer.length < 20) {
    report(onError, "Your TruckX session could not be verified. Sign in again before opening KPI.");
    return false;
  }

  let popup;
  try {
    popup = windowRef.open(KPI_APP_URL, "_blank");
  } catch {
    popup = null;
  }
  if (!popup) {
    report(onError, "The KPI Dashboard window was blocked. Allow pop-ups and try again.");
    return false;
  }

  const attempt = {};
  activeAttempt = attempt;
  let phase = "waiting";
  let expectedChallenge = null;
  let requestController = null;
  let requestTimer = null;
  let acknowledgmentTimer = null;
  let popupMonitor = null;
  let timer = null;
  const release = () => {
    if (activeAttempt === attempt) activeAttempt = null;
  };
  const cleanupWait = () => {
    windowRef.removeEventListener("message", onMessage);
    if (timer !== null) windowRef.clearTimeout(timer);
    timer = null;
  };
  const cleanup = () => {
    cleanupWait();
    if (requestTimer !== null) windowRef.clearTimeout(requestTimer);
    if (acknowledgmentTimer !== null) windowRef.clearTimeout(acknowledgmentTimer);
    if (popupMonitor !== null) windowRef.clearInterval(popupMonitor);
    requestTimer = null;
    acknowledgmentTimer = null;
    popupMonitor = null;
  };
  const popupIsClosed = () => {
    try { return popup.closed === true; } catch { return false; }
  };
  const complete = () => {
    if (phase === "done") return;
    phase = "done";
    cleanup();
    release();
  };
  const fail = (message) => {
    if (phase === "done") return;
    phase = "done";
    cleanup();
    try { requestController?.abort(); } catch { /* Request may already be settled. */ }
    if (!popupIsClosed()) {
      try { popup.close(); } catch { /* The browser may already have closed it. */ }
    }
    release();
    report(onError, message);
  };
  const onMessage = (event) => {
    if (event.origin !== KPI_APP_ORIGIN || event.source !== popup) return;
    const message = event.data;
    if (phase === "awaiting-authentication") {
      if (message?.type === "truckx:kpi-sso-authenticated" && message.challenge === expectedChallenge) complete();
      return;
    }
    if (phase === "waiting" && message?.type === "truckx:kpi-sso-authenticated" && message.challenge === null) {
      // The popup may already have a valid KPI session from an earlier Home handoff.
      complete();
      return;
    }
    if (phase !== "waiting") return;
    if (message?.type !== "truckx:kpi-sso-request" || typeof message.challenge !== "string" || !CHALLENGE_PATTERN.test(message.challenge)) return;

    // A popup challenge is single-use within this handoff. The assertion remains
    // one-time and short-lived; the guard prevents parallel popup exchanges.
    phase = "requesting";
    expectedChallenge = message.challenge;
    if (timer !== null) windowRef.clearTimeout(timer);
    timer = null;
    const Controller = windowRef.AbortController;
    requestController = Controller ? new Controller() : null;
    requestTimer = windowRef.setTimeout(() => fail("KPI sign-in could not reach the verification service. Retry from Home."), requestTimeoutMs);
    fetchImpl(KPI_SSO_ASSERTION_URL, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      ...(requestController ? { signal: requestController.signal } : {}),
      headers: {
        Authorization: `Bearer ${bearer}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ nonce: message.challenge, audience: KPI_APP_ORIGIN })
    }).then(async (response) => {
      if (!response.ok) throw new Error("rejected");
      const result = await response.json();
      if (typeof result?.assertion !== "string" || result.assertion.length < 32 || result.assertion.length > 4096 || result.expiresIn !== 45) {
        throw new Error("invalid response");
      }
      if (phase !== "requesting") return;
      popup.postMessage({
        type: "truckx:kpi-sso-response",
        challenge: message.challenge,
        assertion: result.assertion
      }, KPI_APP_ORIGIN);
      phase = "awaiting-authentication";
      if (requestTimer !== null) windowRef.clearTimeout(requestTimer);
      requestTimer = null;
      acknowledgmentTimer = windowRef.setTimeout(
        () => fail("KPI sign-in could not be confirmed. Retry from Home."),
        acknowledgmentTimeoutMs
      );
    }).catch(() => {
      fail("KPI sign-in could not be completed. Retry from Home; if the problem continues, contact support.");
    });
  };

  timer = windowRef.setTimeout(() => fail("KPI sign-in did not start in time. Retry from Home after the dashboard finishes loading."), timeoutMs);
  windowRef.addEventListener("message", onMessage);
  popupMonitor = windowRef.setInterval(() => {
    if (popupIsClosed()) fail("The KPI window closed before sign-in was confirmed. Retry from Home.");
  }, 500);
  return true;
}
